import React, { useMemo, useState } from "react";
import moment from "moment";
import dayjs from "dayjs";
import { Button, Checkbox, DatePicker, Select, Space, Typography } from "antd";
import { IAvailableSlot, IReservedSlot, getFormattedHours } from "./helper";
import { MAX_FILL_MONTHS, Pattern, applyPattern, laterMonths } from "./slotPattern";

const WEEKDAYS = [
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
  { label: "Sun", value: 0 },
];
const hourOptions = Array.from({ length: 24 }, (_, h) => ({ value: h, label: getFormattedHours(h) }));

// Fills the grid for a whole month from a pattern - "weekdays, 10 AM to 1 PM" -
// instead of clicking every block. It only edits the grid; nothing is saved until
// the user presses Update, so the result can be reviewed and tweaked first.
export const QuickFill: React.FC<{
  month0: number;
  year: number;
  // A getter, not a value: the grid changes without this component re-rendering.
  getCurrent: () => IAvailableSlot[];
  reservedSlots: IReservedSlot[];
  // Slots for the month on screen, plus the pattern so later months can follow on save.
  onApply: (slots: IAvailableSlot[], hours: { from: number; until: number }, pattern: Pattern) => void;
}> = ({ month0, year, getCurrent, reservedSlots, onApply }) => {
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [from, setFrom] = useState(10);
  const [until, setUntil] = useState(13);
  // Last day to fill, inside the month being viewed; empty means the end of it.
  const [lastDay, setLastDay] = useState<string>();

  // Where the fill starts (today, or the 1st of a month still to come) and where it
  // ends: the owner's choice - any date this year - or the end of the month on screen.
  const startDay = useMemo(() => {
    const first = moment([year, month0, 1]);
    return moment.max(first, moment().startOf("day")).format("YYYY-MM-DD");
  }, [month0, year]);
  const endDay = lastDay || moment([year, month0, 1]).endOf("month").format("YYYY-MM-DD");

  const pattern: Pattern = { mode: "add", days, from, until, startDay, lastDay: endDay };

  // Days the pattern lands on, across every month it reaches, for the summary.
  const targets = useMemo(() => {
    const out: moment.Moment[] = [];
    const last = moment(endDay, "YYYY-MM-DD");
    for (let d = moment(startDay, "YYYY-MM-DD"); !d.isAfter(last, "day"); d.add(1, "day")) {
      if (days.includes(d.day())) out.push(d.clone());
    }
    return out;
  }, [startDay, endDay, days]);

  const hours = useMemo(() => Array.from({ length: Math.max(0, until - from) }, (_, i) => from + i), [from, until]);

  const apply = (mode: "add" | "remove") => {
    const p: Pattern = { ...pattern, mode };
    const { slots, changed } = applyPattern(getCurrent(), reservedSlots, p, month0, year);
    onApply(slots, { from, until }, p);
    return changed;
  };

  const [note, setNote] = useState<string>();
  const run = (mode: "add" | "remove") => {
    const n = apply(mode);
    const later = laterMonths({ ...pattern, mode }, month0, year);
    const laterNote = later.length
      ? ` It also covers ${later.map((m) => moment([m.year, m.month0, 1]).format(m.year === year ? "MMM" : "MMM YYYY")).join(", ")}; those months are saved when you press Update.`
      : "";
    setNote(
      n === 0 && !later.length
        ? mode === "add"
          ? "Nothing to add - those slots are already selected, booked, or in the past."
          : "None of those slots were selected."
        : `${mode === "add" ? "Added" : "Removed"} ${n} slot${n === 1 ? "" : "s"} on this month's grid. Review it, then press Update to save.${laterNote}`
    );
  };

  const summary =
    !days.length || until <= from
      ? "Pick at least one day and a time range."
      : `${targets.length} day${targets.length === 1 ? "" : "s"} × ${hours.length} hour${hours.length === 1 ? "" : "s"} (${getFormattedHours(from)} - ${getFormattedHours(until)}) ${
          targets.length ? `from ${targets[0].format("MMM D")} to ${targets[targets.length - 1].format("MMM D")}` : ""
        }`;

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 16, margin: "12px 0", background: "#fafafa" }}>
      <Typography.Text strong style={{ fontSize: 16 }}>
        Quick fill: repeat a time across the month
      </Typography.Text>
      <div style={{ marginTop: 10 }}>
        <Space wrap size="middle">
          <Checkbox.Group options={WEEKDAYS} value={days} onChange={(v) => setDays(v as number[])} />
          <Space>
            <Select style={{ width: 115 }} value={from} options={hourOptions} onChange={setFrom} />
            <span>to</span>
            <Select
              style={{ width: 115 }}
              value={until}
              options={hourOptions.map((o) => ({ ...o, disabled: o.value <= from }))}
              onChange={setUntil}
            />
          </Space>
          <Space>
            <span>Until</span>
            <DatePicker
              placeholder="End of this month"
              allowClear
              disabledDate={(d) => d.isBefore(dayjs(startDay), "day") || d.isAfter(dayjs(startDay).add(MAX_FILL_MONTHS, "month"), "day")}
              defaultPickerValue={dayjs(new Date(year, month0, 1))}
              onChange={(d) => setLastDay(d ? d.format("YYYY-MM-DD") : undefined)}
            />
          </Space>
        </Space>
      </div>
      <div style={{ marginTop: 10 }}>
        <Space wrap>
          <Button type="primary" onClick={() => run("add")} disabled={!days.length || until <= from}>
            Add to calendar
          </Button>
          <Button onClick={() => run("remove")} disabled={!days.length || until <= from}>
            Remove these times
          </Button>
          <Typography.Text type="secondary">{summary}</Typography.Text>
        </Space>
      </div>
      {note && (
        <Typography.Text style={{ display: "block", marginTop: 8 }} type="secondary">
          {note}
        </Typography.Text>
      )}
    </div>
  );
};
